/**
 * background.js — Chat panel background applicator
 *
 * Supports color, image, repeating pattern, and video backgrounds
 * for the messages area. Each is fully optional. When config supplies
 * none, the default surface color (--ws-surface) shows through.
 *
 * Config shapes (all under config.theme.chat_background):
 *
 *   { type: 'color',   color: '#f4ede0' }
 *
 *   { type: 'image',
 *     image_url: 'https://.../bg.jpg',
 *     size:     'cover' | 'contain' | 'auto',   // default: 'cover'
 *     position: 'center' | ...,                 // default: 'center'
 *     repeat:   'no-repeat' | 'repeat' | ...,   // default: 'no-repeat'
 *     color:    '#fff'                          // optional base color
 *   }
 *
 *   { type: 'pattern',
 *     image_url: 'https://.../tile.svg',
 *     size:   'auto',                            // default: 'auto'
 *     repeat: 'repeat',                          // default: 'repeat'
 *     color:  '#faf6ee'                          // tile background
 *   }
 *
 *   { type: 'video',
 *     video_url: 'https://.../loop.mp4',
 *     poster:    'https://.../poster.jpg',
 *     opacity:   0.6,                            // default: 1
 *     muted:     true                            // default: true
 *   }
 *
 * Legacy fallback: theme.chat_background_url (string) is treated as
 *   { type: 'image', image_url: <url> } when chat_background is unset.
 *
 * Returns a cleanup function that removes any DOM/styles applied.
 */

function resolveBackground(config) {
  const theme = config?.theme || {};
  let bg = theme.chat_background;

  if (!bg && theme.chat_background_url) {
    bg = { type: "image", image_url: theme.chat_background_url };
  }
  if (!bg) return null;

  // Allow string shorthand: 'image:<url>' / '#rrggbb'
  if (typeof bg === "string") {
    if (/^#|^rgb|^hsl/i.test(bg)) bg = { type: "color", color: bg };
    else bg = { type: "image", image_url: bg };
  }
  return bg;
}

function applyImageStyles(target, bg, defaults) {
  if (!bg.image_url) return;
  target.style.backgroundImage = `url("${bg.image_url}")`;
  target.style.backgroundRepeat = bg.repeat || defaults.repeat;
  target.style.backgroundSize = bg.size || defaults.size;
  target.style.backgroundPosition = bg.position || "center";
}

export function applyChatBackground(wrapper, content, config) {
  const bg = resolveBackground(config);
  if (!bg || !wrapper) return () => {};

  const cleanups = [];
  const original = {
    bgColor: wrapper.style.backgroundColor,
    bgImage: wrapper.style.backgroundImage,
    bgRepeat: wrapper.style.backgroundRepeat,
    bgSize: wrapper.style.backgroundSize,
    bgPosition: wrapper.style.backgroundPosition,
    position: wrapper.style.position,
  };
  cleanups.push(() => Object.assign(wrapper.style, original));

  if (bg.color) {
    wrapper.style.backgroundColor = bg.color;
  }

  if (bg.type === "image") {
    applyImageStyles(wrapper, bg, { repeat: "no-repeat", size: "cover" });
  } else if (bg.type === "pattern") {
    applyImageStyles(wrapper, bg, { repeat: "repeat", size: "auto" });
  } else if (bg.type === "video" && bg.video_url) {
    wrapper.style.position = wrapper.style.position || "relative";
    if (content) {
      const prevPos = content.style.position;
      const prevZ = content.style.zIndex;
      content.style.position = "relative";
      content.style.zIndex = "1";
      cleanups.push(() => {
        content.style.position = prevPos;
        content.style.zIndex = prevZ;
      });
    }

    const video = document.createElement("video");
    video.className = "ws-bg-video";
    video.src = bg.video_url;
    if (bg.poster) video.poster = bg.poster;
    video.autoplay = true;
    video.loop = true;
    video.muted = bg.muted !== false;
    video.playsInline = true;
    if (typeof bg.opacity === "number") {
      video.style.opacity = String(bg.opacity);
    }
    wrapper.insertBefore(video, wrapper.firstChild);
    cleanups.push(() => video.remove());
  }

  return () => cleanups.forEach((fn) => fn());
}
